import React, {useMemo} from 'react';
import QRCode from 'react-qr-code';
import {format} from 'date-fns';
import {ButtonClipboard, Modal, TextWithLine} from 'components';

import useProjectStore from '../../stores/useProjectStore';

interface IModalQRShareProps {
  visible: boolean;
  onClose: () => void;
  state: 'preview' | 'publish' | undefined;
}

const ModalQRShare = ({visible, onClose, state}: IModalQRShareProps) => {
  const token = useProjectStore(s => s.previewState?.token);
  const isPublished = useProjectStore(s => s.liveState?.published);
  const slug = useProjectStore(s => s.slug);
  const handle = useProjectStore(s => s.creatorHandle) ?? '';

  const previewPublishedAt = useProjectStore(s => s.previewState?.publishedAt);
  const livePublishedAt = useProjectStore(s => s.liveState?.publishedAt);
  const publishedAt = useMemo(() => {
    if (state === 'preview' && token) return previewPublishedAt;
    if (state === 'publish' && isPublished && slug) return livePublishedAt;
  }, [state, previewPublishedAt, livePublishedAt]);

  const previewPublishedBy = useProjectStore(s => s.previewState?.publishedBy);
  const livePublishedBy = useProjectStore(s => s.liveState?.publishedBy);
  const publishedBy = useMemo(() => {
    if (state === 'preview' && token) return previewPublishedBy;
    if (state === 'publish' && isPublished && slug) return livePublishedBy;
  }, [state, previewPublishedBy, livePublishedBy]);

  const link = useMemo(() => {
    if (state === 'preview' && token) return `app.das-ar.com/preview/${handle}/${token}`;
    if (state === 'publish' && isPublished && slug) return `app.das-ar.com/ar/${handle}/${slug}`;
    return '';
  }, [state, isPublished, handle, token, slug]);
  const linkNav = 'https://' + link;

  if (!link) {
    return (
      <Modal visible={visible} onClose={onClose}>
        <div className="flex flex-col items-center gap-3">
          <h2 className="font-semibold text-slate-800 dark:text-white">
            {(() => {
              if (state === 'preview') return 'Project is not saved';
              if (state === 'publish') return 'Project is not published';
              return null;
            })()}
          </h2>
          <p className="text-slate-700 dark:text-white">
            {(() => {
              if (state === 'preview') return 'Make changes to the project and save to generate the QR.';
              if (state === 'publish') return 'Publish the project to generate the QR.';
              return null;
            })()}
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} onClose={onClose}>
      <div className="flex flex-col items-center gap-3">
        <h2 className="font-semibold text-slate-800 dark:text-white">
          {(() => {
            if (state === 'preview') return 'Preview your web AR experience';
            if (state === 'publish') return 'Share your web AR experience';
            return null;
          })()}
        </h2>
        <p className="text-slate-700 dark:text-white">Scan QR</p>
        <div className="p-4 bg-white rounded-lg shadow-md">
          <QRCode value={linkNav} size={200} />
        </div>
        <div className="max-w-xs w-full pt-5 pb-3">
          <TextWithLine text="or continue with" />
        </div>
        <p className="dark:text-white">QR Link</p>
        <div className="flex items-center gap-3 pb-5">
          <a
            href={linkNav}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap text-primary text-sm underline sm:no-underline hover:underline">
            {link}
          </a>
          <ButtonClipboard text={linkNav} />
        </div>
        <div className="flex flex-col items-center gap-1">
          {!!publishedAt && (
            <p className="text-xs text-slate-500 dark:text-darkText">Last updated: {format(new Date(publishedAt), 'hh:mm aaaa, dd MMM y')}</p>
          )}
          {!!publishedBy && <p className="text-xs text-slate-500 dark:text-darkText">by: {publishedBy}</p>}
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(ModalQRShare);
